import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Download, FileText, Zap, Settings, Subtitles, CheckCircle,
  Scissors, Copy, AlertCircle, Boxes, SplitSquareVertical, Edit3, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Shared inline styles (Design System) ── */
const S = {
  card: {
    background: 'var(--dp-bg-card)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: '12px',
    padding: '20px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--dp-bg-input, #252525)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: '8px',
    color: 'var(--dp-text-primary)',
    fontFamily: 'var(--dp-font)',
    fontSize: '13px',
    transition: 'all 0.3s',
    outline: 'none',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--dp-text-secondary)',
    marginBottom: '6px',
    fontFamily: 'var(--dp-font)',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--dp-text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'var(--dp-font)',
  },
  // Padrão premium .dp-btn-cta (gradiente da marca, radius 12, SEM glow) —
  // versão objeto p/ spreads existentes. Ver DESIGN_SYSTEM.md §5.
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '9px 18px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
    border: 'none',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.2px',
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    boxShadow: 'none',
    transition: 'all 0.25s',
  },
  btnSecondary: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--dp-border-medium)',
    background: 'transparent',
    color: 'var(--dp-text-secondary)',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    transition: 'all 0.25s',
  },
  textarea: {
    width: '100%',
    padding: '16px',
    background: 'var(--dp-bg-input)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: '8px',
    color: 'var(--dp-text-primary)',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '13px',
    resize: 'vertical',
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s',
  },
};

const TextToolsPage = ({ onLogout }) => {
  usePageTitle('Ferramentas de Texto');
  const { t } = useTranslation();
  const { theme: themeColors, isDarkMode } = useTheme();
  const { isPrivate } = usePrivacy();
  const { sidebarWidth, isMobile } = useSidebar();
  const [activeTab, setActiveTab] = useState('editor');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Check admin
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then((response) => {
        setIsAdmin(response.data.is_super_admin || false);
        setIsSuperAdmin(response.data.is_super_admin || false);
      })
      .catch(() => {
        setIsAdmin(false);
        setIsSuperAdmin(false);
      });
  }, []);

  // ============= EDITOR STATE =============
  const [texto, setTexto] = useState('');
  const [stats, setStats] = useState({
    caracteres: 0,
    palavras: 0,
    paragrafos: 0,
    tempoNormal: 0,
    tempoLento: 0,
    tempoRapido: 0
  });

  const editorFormatTime = (seconds) => {
    if (seconds === 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}min ${secs}s`;
  };

  useEffect(() => {
    const trimmedText = texto.trim();

    // Caracteres (todos, incluindo espaços)
    const caracteres = texto.length;

    // Palavras
    const palavras = trimmedText ? trimmedText.split(/\s+/).length : 0;

    // Parágrafos (linhas não vazias)
    const paragrafos = trimmedText ? trimmedText.split(/\n+/).filter(p => p.trim().length > 0).length : 0;

    // Tempo de narração (baseado em PALAVRAS)
    // Velocidade Normal: 155 palavras/minuto (padrão para português/vídeos)
    // Lento/Pausado: 130 palavras/minuto
    // Rápido/Dinâmico: 183 palavras/minuto
    const tempoNormal = (palavras / 155) * 60;
    const tempoLento = (palavras / 130) * 60;
    const tempoRapido = (palavras / 183) * 60;

    setStats({
      caracteres,
      palavras,
      paragrafos,
      tempoNormal,
      tempoLento,
      tempoRapido
    });
  }, [texto]);

  const editorDownloadSRT = () => {
    const trimmedText = texto.trim();

    if (!trimmedText) {
      alert(t('textTools.write_text_first_srt'));
      return;
    }

    // Dividir por parágrafos (cada parágrafo = uma legenda)
    const paragraphs = trimmedText.split(/\n\n+/).filter(p => p.trim());

    if (paragraphs.length === 0) {
      alert(t('textTools.no_paragraphs_found'));
      return;
    }

    let srtContent = '';
    let currentTime = 0;

    paragraphs.forEach((paragraph, index) => {
      const words = paragraph.trim().split(/\s+/).length;
      const duration = (words / 155) * 60; // 155 palavras/minuto

      const startHours = Math.floor(currentTime / 3600);
      const startMinutes = Math.floor((currentTime % 3600) / 60);
      const startSeconds = Math.floor(currentTime % 60);
      const startMs = Math.floor((currentTime % 1) * 1000);

      const endTime = currentTime + duration;
      const endHours = Math.floor(endTime / 3600);
      const endMinutes = Math.floor((endTime % 3600) / 60);
      const endSeconds = Math.floor(endTime % 60);
      const endMs = Math.floor((endTime % 1) * 1000);

      srtContent += `${index + 1}\n`;
      srtContent += `${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}:${String(startSeconds).padStart(2, '0')},${String(startMs).padStart(3, '0')} --> `;
      srtContent += `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:${String(endSeconds).padStart(2, '0')},${String(endMs).padStart(3, '0')}\n`;
      srtContent += `${paragraph.trim()}\n\n`;

      currentTime = endTime;
    });

    const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'legenda.srt';
    link.click();
  };

  const editorDownloadTXT = () => {
    const trimmedText = texto.trim();

    if (!trimmedText) {
      alert(t('textTools.write_text_first'));
      return;
    }

    const blob = new Blob([trimmedText], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'texto.txt';
    link.click();
  };

  // ============= SRT STATE =============
  const [srtText, setSrtText] = useState('');
  const [srtContent, setSrtContent] = useState('');
  const [srtFileName, setSrtFileName] = useState('legendas');
  const [showConfig, setShowConfig] = useState(false);
  const [srtConfig, setSrtConfig] = useState({
    charsPerSecond: 11,
    minDuration: 2,
    maxDuration: 60,
    pauseBetween: 1,
    maxCharsPerLine: 495,
    sentencesPerSegment: 8,
    safetyMargin: 1.2
  });

  const srtPresets = {
    maximo: {
      maxDuration: 60,
      maxCharsPerLine: 495,
      sentencesPerSegment: 8,
      charsPerSecond: 11,
      pauseBetween: 1,
      safetyMargin: 1.2,
      name: t('textTools.max_capcut') + " (495 chars)"
    },
    medio: {
      maxDuration: 40,
      maxCharsPerLine: 350,
      sentencesPerSegment: 5,
      charsPerSecond: 11,
      pauseBetween: 1,
      safetyMargin: 1.2,
      name: t('textTools.medium') + " (350 chars)"
    },
    curto: {
      maxDuration: 25,
      maxCharsPerLine: 250,
      sentencesPerSegment: 3,
      charsPerSecond: 11,
      pauseBetween: 1,
      safetyMargin: 1.2,
      name: t('textTools.short') + " (250 chars)"
    },
    muito_curto: {
      maxDuration: 15,
      maxCharsPerLine: 150,
      sentencesPerSegment: 2,
      charsPerSecond: 11,
      pauseBetween: 1,
      safetyMargin: 1.2,
      name: t('textTools.very_short') + " (150 chars)"
    }
  };

  const srtApplyPreset = (presetKey) => {
    const preset = srtPresets[presetKey];
    setSrtConfig(prev => ({
      ...prev,
      maxDuration: preset.maxDuration,
      maxCharsPerLine: preset.maxCharsPerLine,
      sentencesPerSegment: preset.sentencesPerSegment,
      charsPerSecond: preset.charsPerSecond,
      pauseBetween: preset.pauseBetween,
      safetyMargin: preset.safetyMargin
    }));
    toast.success(t('textTools.preset_applied', { name: preset.name }));
  };

  useEffect(() => {
    // Apply default preset on mount
    setSrtConfig(prev => ({
      ...prev,
      maxDuration: 60,
      maxCharsPerLine: 495,
      sentencesPerSegment: 8,
      charsPerSecond: 11,
      pauseBetween: 1,
      safetyMargin: 1.2
    }));
  }, []);

  const srtFormatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
  };

  // Helper function: Find safe break point in text (after punctuation, never before) - SRT version
  const srtFindSafeBreakPoint = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text.length;
    }

    // Define punctuation marks that are safe to break AFTER
    // Priority order: period > comma > semicolon > colon > dash
    const punctuationMarks = [
      { char: '.', priority: 1 },
      { char: '!', priority: 1 },
      { char: '?', priority: 1 },
      { char: ',', priority: 2 },
      { char: ';', priority: 3 },
      { char: ':', priority: 3 },
      { char: '\u2014', priority: 4 },
      { char: '-', priority: 4 },
      { char: ')', priority: 5 }
    ];

    // Search backwards from maxLength to find the best break point
    let bestBreakPoint = -1;
    let bestPriority = 999;

    for (let i = maxLength; i >= Math.floor(maxLength * 0.5); i--) {
      const char = text[i];

      // Check if this character is a punctuation mark
      const punctuation = punctuationMarks.find(p => p.char === char);

      if (punctuation) {
        // Found a punctuation mark! Check if it's followed by a space (natural break)
        const nextChar = text[i + 1];

        if (nextChar === ' ' || nextChar === undefined) {
          // This is a valid break point (after punctuation + space)
          if (punctuation.priority < bestPriority) {
            bestBreakPoint = i + 1; // Break AFTER the punctuation
            bestPriority = punctuation.priority;

            // If we found a period/question/exclamation, that's the best - stop searching
            if (punctuation.priority === 1) {
              break;
            }
          }
        }
      }
    }

    // If we found a good break point, use it
    if (bestBreakPoint > 0) {
      return bestBreakPoint;
    }

    // Fallback: break at last space (but this means no punctuation was found)
    for (let i = maxLength; i >= Math.floor(maxLength * 0.5); i--) {
      if (text[i] === ' ') {
        return i;
      }
    }

    // Last resort: break at maxLength (avoid cutting words)
    return maxLength;
  };

  const generateSRT = () => {
    if (!srtText.trim()) {
      toast.error(t('textTools.insert_text_error'));
      return;
    }

    // Split text into sentences preserving punctuation
    let sentences = srtText
      .replace(/([.!?])\s+/g, '$1\n')
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const subtitles = [];
    let currentTime = 0;
    let segmentIndex = 0;

    while (segmentIndex < sentences.length) {
      let segmentSentences = [];
      let segmentText = '';
      let sentenceCount = 0;

      // Build segment by adding sentences until we hit limits
      while (segmentIndex + sentenceCount < sentences.length &&
             sentenceCount < srtConfig.sentencesPerSegment) {

        const nextSentence = sentences[segmentIndex + sentenceCount];
        const testText = segmentSentences.length === 0
          ? nextSentence
          : segmentText + ' ' + nextSentence;

        // Check if adding this sentence would exceed character limit
        if (testText.length > srtConfig.maxCharsPerLine && segmentSentences.length > 0) {
          // Don't add this sentence, use what we have
          break;
        }

        // Add the sentence to the segment
        segmentSentences.push(nextSentence);
        segmentText = testText;
        sentenceCount++;
      }

      // If no sentences were added (single sentence too long), split it intelligently
      if (segmentSentences.length === 0) {
        const longSentence = sentences[segmentIndex];

        // If sentence exceeds limit, split at safe punctuation point
        if (longSentence.length > srtConfig.maxCharsPerLine) {
          // Use smart break point finder
          const breakPoint = srtFindSafeBreakPoint(longSentence, srtConfig.maxCharsPerLine);

          segmentText = longSentence.substring(0, breakPoint).trim();

          // Create a modified sentence for the remainder
          const remainder = longSentence.substring(breakPoint).trim();
          if (remainder.length > 0) {
            // Insert remainder back into sentences array for next iteration
            sentences.splice(segmentIndex + 1, 0, remainder);
          }
        } else {
          segmentText = longSentence;
        }

        sentenceCount = 1;
      }

      // SAFETY CHECK: Ensure text never exceeds limit (with smart breaking)
      if (segmentText.length > srtConfig.maxCharsPerLine) {
        const breakPoint = srtFindSafeBreakPoint(segmentText, srtConfig.maxCharsPerLine);
        segmentText = segmentText.substring(0, breakPoint).trim();
      }

      // Calculate duration for this segment
      const duration = Math.min(
        (segmentText.length / srtConfig.charsPerSecond) * srtConfig.safetyMargin,
        srtConfig.maxDuration
      );

      // Add subtitle entry
      subtitles.push({
        index: subtitles.length + 1,
        start: currentTime,
        end: currentTime + duration,
        text: segmentText
      });

      // Move to next segment
      currentTime += duration + srtConfig.pauseBetween;
      segmentIndex += sentenceCount;
    }

    const srtTextResult = subtitles.map(sub =>
      `${sub.index}\n${srtFormatTime(sub.start)} --> ${srtFormatTime(sub.end)}\n${sub.text}\n`
    ).join('\n');

    setSrtContent(srtTextResult);
    toast.success(t('textTools.subtitles_generated', { count: subtitles.length }));
  };

  const srtDownloadSRT = () => {
    if (!srtContent) {
      toast.error(t('textTools.generate_first'));
      return;
    }

    const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Usar nome definido pelo usuário, sanitizar e adicionar extensão .srt
    const sanitizedName = (srtFileName || 'legendas')
      .replace(/[<>:"/\\|?*]/g, '')  // Remover caracteres inválidos
      .replace(/\s+/g, '_')           // Substituir espaços por underscore
      .substring(0, 100);             // Limitar tamanho

    link.download = `${sanitizedName}.srt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(t('textTools.file_downloaded'));
  };

  // ============= DIVIDER STATE =============
  const [textoOriginal, setTextoOriginal] = useState('');
  const [limiteCaracteres, setLimiteCaracteres] = useState(500);
  const [partesDivididas, setPartesDivididas] = useState([]);
  const [textoDividido, setTextoDividido] = useState('');
  const [copiedIndices, setCopiedIndices] = useState([]);
  const [copiedAll, setCopiedAll] = useState(false);
  const [modoAtivo, setModoAtivo] = useState('partes'); // 'partes' ou 'blocos'
  const [loading, setLoading] = useState(false);

  // Helper function: Find safe break point in text (after punctuation, never before) - Divider version (with startPos)
  const dividerFindSafeBreakPoint = (text, maxLength, startPos = 0) => {
    const endPos = startPos + maxLength;

    // If we're at or past the end of text, return the end
    if (endPos >= text.length) {
      return text.length;
    }

    // Define punctuation marks that are safe to break AFTER
    const punctuationMarks = [
      { char: '.', priority: 1 },
      { char: '!', priority: 1 },
      { char: '?', priority: 1 },
      { char: ',', priority: 2 },
      { char: ';', priority: 3 },
      { char: ':', priority: 3 },
      { char: '\u2014', priority: 4 },
      { char: '-', priority: 4 },
      { char: ')', priority: 5 }
    ];

    // Search backwards from endPos to find the best break point
    let bestBreakPoint = -1;
    let bestPriority = 999;
    const searchStart = Math.max(startPos, endPos - Math.floor(maxLength * 0.5));

    for (let i = endPos - 1; i >= searchStart; i--) {
      if (i < startPos || i >= text.length) continue;

      const char = text[i];

      // Check if this character is a punctuation mark
      const punctuation = punctuationMarks.find(p => p.char === char);

      if (punctuation) {
        // Check if it's followed by a space or end of text (natural break)
        const nextChar = i + 1 < text.length ? text[i + 1] : null;

        if (nextChar === ' ' || nextChar === null) {
          // This is a valid break point (after punctuation + space)
          if (punctuation.priority < bestPriority) {
            bestBreakPoint = i + 1; // Break AFTER the punctuation
            bestPriority = punctuation.priority;

            // If we found a period/question/exclamation, that's the best
            if (punctuation.priority === 1) {
              break;
            }
          }
        }
      }
    }

    // If we found a good break point, use it
    if (bestBreakPoint > startPos) {
      return bestBreakPoint;
    }

    // Fallback: break at last space
    for (let i = endPos - 1; i >= searchStart; i--) {
      if (i < startPos || i >= text.length) continue;
      if (text[i] === ' ') {
        return i;
      }
    }

    // Last resort: break at endPos
    return endPos;
  };

  // Modelo 1: Divisor por Partes (Original com quebra inteligente)
  const dividirTextoPorPartes = () => {
    if (!textoOriginal.trim()) {
      toast.error(t('textTools.paste_text_first'));
      return;
    }

    if (limiteCaracteres < 100) {
      toast.error(t('textTools.min_chars_100'));
      return;
    }

    // Remove quebras de linha e múltiplos espaços, deixando o texto corrido
    const textoProcessado = textoOriginal.split(/\s+/).join(' ').trim();
    const partes = [];
    let inicio = 0;

    while (inicio < textoProcessado.length) {
      // Use intelligent break point finder
      const fim = dividerFindSafeBreakPoint(textoProcessado, limiteCaracteres, inicio);

      const parte = textoProcessado.substring(inicio, fim).trim();
      if (parte) {
        partes.push(parte);
      }

      // Move to next part, skipping the space/punctuation
      inicio = fim;
      if (inicio < textoProcessado.length && textoProcessado[inicio] === ' ') {
        inicio++;
      }
    }

    setPartesDivididas(partes);
    setTextoDividido('');
    toast.success(t('textTools.text_split_success', { count: partes.length }));
  };

  // Modelo 2: Divisor por Blocos (CapCut)
  const dividirTextoPorBlocos = async () => {
    if (!textoOriginal.trim()) {
      toast.error(t('textTools.paste_text_first'));
      return;
    }

    if (limiteCaracteres < 50) {
      toast.error(t('textTools.min_chars_50'));
      return;
    }

    if (limiteCaracteres > 10000) {
      toast.error(t('textTools.max_chars_10000'));
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/divisor-texto/blocos`,
        {
          texto: textoOriginal,
          tamanho_bloco: limiteCaracteres
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setTextoDividido(response.data.texto_dividido);
      setPartesDivididas(response.data.blocos);
      toast.success(t('textTools.blocks_split_success', { count: response.data.total_blocos }));
    } catch (error) {
      console.error('Erro ao dividir texto:', error);
      toast.error(error.response?.data?.detail || t('textTools.split_error'));
    } finally {
      setLoading(false);
    }
  };

  const dividirTexto = () => {
    // Limpar índices copiados ao gerar novo texto
    setCopiedIndices([]);
    setCopiedAll(false);

    if (modoAtivo === 'partes') {
      dividirTextoPorPartes();
    } else {
      dividirTextoPorBlocos();
    }
  };

  const copiarParte = (textoParaCopiar, index) => {
    navigator.clipboard.writeText(textoParaCopiar).then(() => {
      // Adicionar índice ao array de copiados se ainda não estiver lá
      setCopiedIndices(prev => {
        if (!prev.includes(index)) {
          return [...prev, index];
        }
        return prev;
      });
      toast.success(modoAtivo === 'partes'
        ? t('textTools.part_copied', { index: index + 1 })
        : t('textTools.block_copied', { index: index + 1 })
      );
    }).catch(() => {
      toast.error(t('textTools.copy_error'));
    });
  };

  const copiarTudo = () => {
    const textoParaCopiar = modoAtivo === 'blocos' ? textoDividido : partesDivididas.join('\n\n');

    navigator.clipboard.writeText(textoParaCopiar).then(() => {
      setCopiedAll(true);
      toast.success(t('textTools.full_text_copied'));

      setTimeout(() => {
        setCopiedAll(false);
      }, 2000);
    }).catch(() => {
      toast.error(t('textTools.copy_error'));
    });
  };

  const limparTudo = () => {
    setTextoOriginal('');
    setPartesDivididas([]);
    setTextoDividido('');
    setCopiedIndices([]);
    toast.success(t('textTools.all_cleared'));
  };

  // ============= TABS CONFIG =============
  const tabs = [
    { key: 'editor', icon: <Edit3 size={15} />, label: t('textTools.editor_tab') },
    { key: 'srt', icon: <MessageSquare size={15} />, label: t('textTools.srt_tab') },
    { key: 'divider', icon: <Scissors size={15} />, label: t('textTools.divider_tab') },
  ];

  // ============= RENDER EDITOR =============
  const renderEditor = () => (
    <>
      {/* Statistics Cards */}
      <div className="cq-grid-3" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {/* Caracteres */}
        <div className="dp-top-accent" style={S.card}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--dp-text-secondary)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {t('textTools.characters')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--dp-blue-500)',
            fontFamily: 'var(--dp-font)',
          }}>
            {stats.caracteres.toLocaleString('pt-BR')}
          </div>
        </div>

        {/* Palavras */}
        <div className="dp-top-accent" style={S.card}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--dp-text-secondary)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {t('textTools.words')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--dp-blue-500)',
            fontFamily: 'var(--dp-font)',
          }}>
            {stats.palavras.toLocaleString('pt-BR')}
          </div>
        </div>

        {/* Parágrafos */}
        <div className="dp-top-accent" style={S.card}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--dp-text-secondary)',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {t('textTools.paragraphs')}
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--dp-blue-500)',
            fontFamily: 'var(--dp-font)',
          }}>
            {stats.paragrafos.toLocaleString('pt-BR')}
          </div>
        </div>
      </div>

      {/* Tempo de Narracao */}
      <div className="dp-top-accent" style={{ ...S.card, marginBottom: '24px' }}>
        <h3 style={{
          ...S.sectionTitle,
          marginBottom: '16px',
        }}>
          <Zap size={18} style={{ color: 'var(--dp-blue-500)' }} />
          {t('textTools.narration_time')}
        </h3>
        <div className="cq-grid-3" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
          {/* Normal */}
          <div style={{
            padding: '16px',
            borderRadius: '10px',
            background: 'rgba(13, 174, 255, 0.06)',
            border: '1px solid rgba(13, 174, 255, 0.12)',
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--dp-blue-500)',
              marginBottom: '8px',
            }}>
              {t('textTools.normal')}
            </div>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: 'var(--dp-text-primary)',
            }}>
              {editorFormatTime(stats.tempoNormal)}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--dp-text-tertiary)',
              marginTop: '4px',
            }}>
              155 {t('textTools.words_per_min')}
            </div>
          </div>

          {/* Lento/Pausado */}
          <div style={{
            padding: '16px',
            borderRadius: '10px',
            background: 'rgba(251, 191, 36, 0.06)',
            border: '1px solid rgba(251, 191, 36, 0.12)',
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--dp-yellow-500, #FBBF24)',
              marginBottom: '8px',
            }}>
              {t('textTools.slow')}
            </div>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: 'var(--dp-text-primary)',
            }}>
              {editorFormatTime(stats.tempoLento)}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--dp-text-tertiary)',
              marginTop: '4px',
            }}>
              130 {t('textTools.words_per_min')}
            </div>
          </div>

          {/* Rapido/Dinamico */}
          <div style={{
            padding: '16px',
            borderRadius: '10px',
            background: 'rgba(52, 211, 153, 0.06)',
            border: '1px solid rgba(52, 211, 153, 0.12)',
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--dp-green-500, #34D399)',
              marginBottom: '8px',
            }}>
              {t('textTools.fast')}
            </div>
            <div style={{
              fontSize: '22px',
              fontWeight: 700,
              color: 'var(--dp-text-primary)',
            }}>
              {editorFormatTime(stats.tempoRapido)}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--dp-text-tertiary)',
              marginTop: '4px',
            }}>
              183 {t('textTools.words_per_min')}
            </div>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div style={{
        background: 'var(--dp-bg-card)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ ...S.sectionTitle }}>
            {t('textTools.text_area')}
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={editorDownloadSRT}
              style={{
                ...S.btnPrimary,
                padding: '7px 14px',
                fontSize: '12px',
              }}
            >
              <Download size={14} />
              {t('textTools.download_srt')}
            </button>
            <button
              onClick={editorDownloadTXT}
              style={{
                ...S.btnSecondary,
              }}
            >
              <FileText size={14} />
              {t('textTools.download_txt')}
            </button>
          </div>
        </div>

        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={t('textTools.type_or_paste')}
          style={{
            ...S.textarea,
            minHeight: '400px',
            borderRadius: 0,
            border: 'none',
            resize: 'none',
          }}
        />
      </div>

      {/* Dicas */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        borderRadius: '12px',
        background: 'rgba(13, 174, 255, 0.05)',
        border: '1px solid rgba(13, 174, 255, 0.10)',
      }}>
        <h4 style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--dp-blue-500)',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <AlertCircle size={14} />
          {t('textTools.tips_title')}
        </h4>
        <ul style={{
          fontSize: '12px',
          color: 'var(--dp-text-secondary)',
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ color: 'var(--dp-blue-500)', marginTop: '2px' }}>&#8226;</span>
            <span>{t('textTools.tip_srt')}</span>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ color: 'var(--dp-blue-500)', marginTop: '2px' }}>&#8226;</span>
            <span>{t('textTools.tip_txt')}</span>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ color: 'var(--dp-blue-500)', marginTop: '2px' }}>&#8226;</span>
            <span>{t('textTools.tip_narration')}</span>
          </li>
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ color: 'var(--dp-blue-500)', marginTop: '2px' }}>&#8226;</span>
            <span>{t('textTools.tip_paragraphs')}</span>
          </li>
        </ul>
      </div>
    </>
  );

  // ============= RENDER SRT =============
  const renderSRT = () => (
    <>
      {/* Presets */}
      <div className="dp-top-accent" style={{ ...S.card, marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <h3 style={{ ...S.sectionTitle }}>
            <Zap size={18} style={{ color: 'var(--dp-blue-500)' }} />
            {t('textTools.quick_presets')}
          </h3>

          <button
            onClick={() => setShowConfig(!showConfig)}
            style={{ ...S.btnSecondary }}
          >
            <Settings size={14} />
            {showConfig ? t('textTools.hide_config') : t('textTools.settings')}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {Object.entries(srtPresets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => srtApplyPreset(key)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'var(--dp-bg-input)',
                color: 'var(--dp-text-secondary)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: 'var(--dp-font)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                e.currentTarget.style.color = 'var(--dp-blue-500)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                e.currentTarget.style.color = 'var(--dp-text-secondary)';
              }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <div className="dp-top-accent" style={{ ...S.card, marginBottom: '16px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>
            <Settings size={18} style={{ color: 'var(--dp-blue-500)' }} />
            {t('textTools.advanced_settings')}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div>
              <label style={S.label}>
                {t('textTools.max_duration')}
              </label>
              <input
                type="number"
                value={srtConfig.maxDuration}
                onChange={(e) => setSrtConfig({ ...srtConfig, maxDuration: Number(e.target.value) })}
                style={S.input}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label style={S.label}>
                {t('textTools.chars_per_line')}
              </label>
              <input
                type="number"
                min="50"
                max="500"
                value={srtConfig.maxCharsPerLine}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value > 500) {
                    toast.error(t('textTools.capcut_limit_error'));
                    setSrtConfig({ ...srtConfig, maxCharsPerLine: 500 });
                  } else {
                    setSrtConfig({ ...srtConfig, maxCharsPerLine: value });
                  }
                }}
                style={S.input}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label style={S.label}>
                {t('textTools.sentences_per_segment')}
              </label>
              <input
                type="number"
                value={srtConfig.sentencesPerSegment}
                onChange={(e) => setSrtConfig({ ...srtConfig, sentencesPerSegment: Number(e.target.value) })}
                style={S.input}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Input and Output Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        {/* Text Input */}
        <div className="dp-top-accent" style={S.card}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>
            <FileText size={18} style={{ color: 'var(--dp-blue-500)' }} />
            {t('textTools.original_text')}
          </h3>

          <textarea
            value={srtText}
            onChange={(e) => setSrtText(e.target.value)}
            placeholder={t('textTools.paste_text')}
            style={{
              ...S.textarea,
              minHeight: '250px',
              maxHeight: '400px',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />

          <button
            onClick={generateSRT}
            disabled={!srtText.trim()}
            style={{
              ...S.btnPrimary,
              width: '100%',
              marginTop: '12px',
              opacity: srtText.trim() ? 1 : 0.5,
              cursor: srtText.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            <CheckCircle size={16} />
            {t('textTools.generate_subtitles')}
          </button>
        </div>

        {/* SRT Output */}
        <div className="dp-top-accent" style={S.card}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>
            <Subtitles size={18} style={{ color: 'var(--dp-blue-500)' }} />
            {t('textTools.srt_file')}
          </h3>

          <textarea
            value={srtContent}
            readOnly
            placeholder={t('textTools.subtitles_appear')}
            style={{
              ...S.textarea,
              minHeight: '250px',
              maxHeight: '400px',
              cursor: 'default',
            }}
          />

          {/* Nome do arquivo */}
          <div style={{ marginTop: '12px' }}>
            <label style={S.label}>
              {t('textTools.filename')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={srtFileName}
                onChange={(e) => setSrtFileName(e.target.value)}
                placeholder={t('textTools.filename')}
                style={{ ...S.input, paddingRight: '40px' }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '13px',
                color: 'var(--dp-text-tertiary)',
              }}>
                .srt
              </span>
            </div>
          </div>

          <button
            onClick={srtDownloadSRT}
            disabled={!srtContent}
            style={{
              ...S.btnPrimary,
              width: '100%',
              marginTop: '12px',
              opacity: srtContent ? 1 : 0.5,
              cursor: srtContent ? 'pointer' : 'not-allowed',
            }}
          >
            <Download size={16} />
            {t('textTools.download_srt')}
          </button>
        </div>
      </div>

      {/* Info Alert */}
      <div style={{
        marginTop: '16px',
        padding: '14px 16px',
        borderRadius: '10px',
        background: 'rgba(13, 174, 255, 0.05)',
        border: '1px solid rgba(13, 174, 255, 0.10)',
      }}>
        <p style={{
          fontSize: '12px',
          color: 'var(--dp-text-secondary)',
          margin: 0,
          lineHeight: '1.6'
        }}>
          <strong style={{ color: 'var(--dp-blue-500)' }}>{t('textTools.tip')}:</strong> {t('textTools.tip_text')}
        </p>
      </div>
    </>
  );

  // ============= RENDER DIVIDER =============
  const renderDivider = () => (
    <>
      {/* Mode Selector */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          gap: '6px',
          background: 'var(--dp-bg-card)',
          padding: '5px',
          borderRadius: '12px',
          width: 'fit-content',
          border: '1px solid var(--dp-border-subtle)',
        }}>
          <button
            onClick={() => {
              setModoAtivo('partes');
              setPartesDivididas([]);
              setTextoDividido('');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: modoAtivo === 'partes'
                ? 'linear-gradient(135deg, rgba(13, 174, 255, 0.12), rgba(13, 174, 255, 0.04))'
                : 'transparent',
              color: modoAtivo === 'partes' ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
              fontSize: '13px',
              fontWeight: modoAtivo === 'partes' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.25s',
              fontFamily: 'var(--dp-font)',
              boxShadow: modoAtivo === 'partes'
                ? '0 0 14px rgba(13, 174, 255, 0.10)'
                : 'none',
            }}
          >
            <SplitSquareVertical size={16} />
            {t('textTools.split_by_parts')}
          </button>

          <button
            onClick={() => {
              setModoAtivo('blocos');
              setPartesDivididas([]);
              setTextoDividido('');
              setLimiteCaracteres(500);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: modoAtivo === 'blocos'
                ? 'linear-gradient(135deg, rgba(13, 174, 255, 0.12), rgba(13, 174, 255, 0.04))'
                : 'transparent',
              color: modoAtivo === 'blocos' ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
              fontSize: '13px',
              fontWeight: modoAtivo === 'blocos' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.25s',
              fontFamily: 'var(--dp-font)',
              boxShadow: modoAtivo === 'blocos'
                ? '0 0 14px rgba(13, 174, 255, 0.10)'
                : 'none',
            }}
          >
            <Boxes size={16} />
            {t('textTools.split_by_blocks')}
          </button>
        </div>

        {/* Description based on mode */}
        <div style={{
          marginTop: '12px',
          padding: '12px 16px',
          borderRadius: '10px',
          background: 'rgba(13, 174, 255, 0.05)',
          border: '1px solid rgba(13, 174, 255, 0.10)',
        }}>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: 'var(--dp-text-secondary)',
            lineHeight: '1.6'
          }}>
            {modoAtivo === 'partes' ? (
              <>
                <strong style={{ color: 'var(--dp-text-primary)' }}>{t('textTools.split_by_parts')}:</strong> {t('textTools.split_parts_desc')}
              </>
            ) : (
              <>
                <strong style={{ color: 'var(--dp-text-primary)' }}>{t('textTools.split_by_blocks')}:</strong> {t('textTools.split_blocks_desc')}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Input Section */}
      <div className="dp-top-accent" style={{ ...S.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Texto Input */}
          <div>
            <label style={S.label}>
              {modoAtivo === 'partes' ? t('textTools.paste_large_text') : t('textTools.paste_blocks_text')}
            </label>
            <textarea
              value={textoOriginal}
              onChange={(e) => setTextoOriginal(e.target.value)}
              placeholder={modoAtivo === 'partes'
                ? t('textTools.paste_large_text')
                : t('textTools.paste_blocks_text')
              }
              style={{
                ...S.textarea,
                minHeight: '200px',
                maxHeight: '350px',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
              fontSize: '11px',
              color: 'var(--dp-text-tertiary)',
            }}>
              <span>{textoOriginal.length.toLocaleString('pt-BR')} {t('textTools.characters').toLowerCase()}</span>
            </div>
          </div>

          {/* Limite Input */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={S.label}>
                {modoAtivo === 'partes' ? t('textTools.char_limit') : t('textTools.block_size')}
              </label>
              <input
                type="number"
                min={modoAtivo === 'partes' ? 100 : 50}
                max={modoAtivo === 'partes' ? 50000 : 10000}
                value={limiteCaracteres}
                onChange={(e) => setLimiteCaracteres(parseInt(e.target.value) || (modoAtivo === 'partes' ? 4000 : 500))}
                style={S.input}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                {modoAtivo === 'partes' ? (
                  <>{t('textTools.suggestions_parts')}</>
                ) : (
                  <>{t('textTools.suggestions_blocks')}</>
                )}
              </div>
            </div>

            <div>
              <label style={{ ...S.label, color: 'transparent', userSelect: 'none' }}>
                Placeholder
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={dividirTexto}
                  disabled={loading}
                  style={{
                    ...S.btnPrimary,
                    flex: 1,
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <Scissors size={14} />
                  {loading ? t('textTools.splitting') : t('textTools.split')}
                </button>
                <button
                  onClick={limparTudo}
                  style={S.btnSecondary}
                >
                  {t('textTools.clear')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {(partesDivididas.length > 0 || textoDividido) && (
        <div className="dp-top-accent" style={S.card}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <h2 style={{ ...S.sectionTitle }}>
              <FileText size={18} style={{ color: 'var(--dp-blue-500)' }} />
              {modoAtivo === 'partes' ? t('textTools.divided_text_parts') : t('textTools.divided_text_blocks')}
            </h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 500,
                background: 'rgba(13, 174, 255, 0.10)',
                color: 'var(--dp-blue-500)',
              }}>
                {partesDivididas.length} {modoAtivo === 'partes' ? t('textTools.parts_created').toLowerCase() : t('textTools.blocks_created').toLowerCase()}
              </span>
              <button
                onClick={copiarTudo}
                style={{
                  ...S.btnPrimary,
                  padding: '6px 14px',
                  fontSize: '12px',
                  background: copiedAll
                    ? 'linear-gradient(135deg, var(--dp-green-500, #34D399), #059669)'
                    : 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                }}
              >
                {copiedAll ? (
                  <><CheckCircle size={14} /> {t('textTools.all_copied')}</>
                ) : (
                  <><Copy size={14} /> {t('textTools.copy_all')}</>
                )}
              </button>
            </div>
          </div>

          {/* Display based on mode */}
          {modoAtivo === 'blocos' && textoDividido && (
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              borderRadius: '8px',
              background: 'var(--dp-bg-input)',
              border: '1px solid var(--dp-border-subtle)',
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 500,
                color: 'var(--dp-text-tertiary)',
                marginBottom: '8px',
              }}>
                {t('textTools.full_text_ready')}
              </div>
              <pre style={{
                fontSize: '11px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'ui-monospace, monospace',
                color: 'var(--dp-text-secondary)',
                maxHeight: '300px',
                overflow: 'auto',
                margin: 0,
              }}>
                {textoDividido}
              </pre>
            </div>
          )}

          {/* List of parts/blocks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {modoAtivo === 'partes' ? (
              /* Modo Partes - Layout horizontal simples */
              partesDivididas.map((parte, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'var(--dp-bg-input)',
                    border: '1px solid var(--dp-border-subtle)',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  }}
                >
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                    background: 'rgba(13, 174, 255, 0.10)',
                    color: 'var(--dp-blue-500)',
                  }}>
                    {index + 1}
                  </span>

                  <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                    Texto
                  </span>

                  <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
                    ({parte.length.toLocaleString('pt-BR')} {t('textTools.characters').toLowerCase()})
                  </span>

                  <button
                    onClick={() => copiarParte(parte, index)}
                    style={{
                      ...S.btnSecondary,
                      marginLeft: 'auto',
                      flexShrink: 0,
                      padding: '6px 12px',
                      fontSize: '11px',
                      ...(copiedIndices.includes(index) ? {
                        borderColor: 'var(--dp-green-500, #34D399)',
                        color: 'var(--dp-green-500, #34D399)',
                        background: 'rgba(52, 211, 153, 0.08)',
                      } : {}),
                    }}
                  >
                    {copiedIndices.includes(index) ? (
                      <><CheckCircle size={14} /> {t('textTools.copied')}</>
                    ) : (
                      <><Copy size={14} /> {t('textTools.copy')}</>
                    )}
                  </button>
                </div>
              ))
            ) : (
              /* Modo Blocos - Agrupamento por PALAVRAS (ate 3000 palavras) */
              (() => {
                // Funcao para contar palavras
                const contarPalavras = (textoContagem) => {
                  return textoContagem.trim().split(/\s+/).filter(word => word.length > 0).length;
                };

                // Agrupar blocos em partes de ate 3000 PALAVRAS
                const partes = [];
                let parteAtual = [];
                let palavrasAtual = 0;

                partesDivididas.forEach((bloco) => {
                  const palavrasBloco = contarPalavras(bloco);

                  if (palavrasAtual + palavrasBloco <= 3000) {
                    parteAtual.push(bloco);
                    palavrasAtual += palavrasBloco;
                  } else {
                    if (parteAtual.length > 0) {
                      partes.push({ blocos: parteAtual, palavras: palavrasAtual });
                    }
                    parteAtual = [bloco];
                    palavrasAtual = palavrasBloco;
                  }
                });

                if (parteAtual.length > 0) {
                  partes.push({ blocos: parteAtual, palavras: palavrasAtual });
                }

                return partes.map((parte, parteIndex) => {
                  const textoParaCopiar = parte.blocos.join('\n');
                  const totalPalavras = parte.palavras;

                  return (
                    <div
                      key={parteIndex}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px',
                        borderRadius: '10px',
                        background: 'var(--dp-bg-input)',
                        border: '1px solid var(--dp-border-subtle)',
                        transition: 'border-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                      }}
                    >
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        flexShrink: 0,
                        background: 'rgba(13, 174, 255, 0.10)',
                        color: 'var(--dp-blue-500)',
                      }}>
                        {parteIndex + 1}
                      </span>

                      <span style={{ fontSize: '13px', color: 'var(--dp-text-secondary)' }}>
                        {t('textTools.part_label', { index: parteIndex + 1 })}
                      </span>

                      <span style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)' }}>
                        ({totalPalavras.toLocaleString('pt-BR')} {t('textTools.words').toLowerCase()})
                      </span>

                      <button
                        onClick={() => {
                          const partKey = `parte-${parteIndex}`;
                          navigator.clipboard.writeText(textoParaCopiar).then(() => {
                            setCopiedIndices(prev => {
                              if (!prev.includes(partKey)) {
                                return [...prev, partKey];
                              }
                              return prev;
                            });
                            toast.success(t('textTools.part_copied', { index: parteIndex + 1 }));
                          }).catch(() => toast.error(t('textTools.copy_error')));
                        }}
                        style={{
                          ...S.btnSecondary,
                          marginLeft: 'auto',
                          flexShrink: 0,
                          ...(copiedIndices.includes(`parte-${parteIndex}`) ? {
                            borderColor: 'var(--dp-green-500, #34D399)',
                            color: 'var(--dp-green-500, #34D399)',
                            background: 'rgba(52, 211, 153, 0.08)',
                          } : {}),
                        }}
                      >
                        {copiedIndices.includes(`parte-${parteIndex}`) ? (
                          <><CheckCircle size={14} /> {t('textTools.copied')}</>
                        ) : (
                          <><Copy size={14} /> {t('textTools.copy_part')}</>
                        )}
                      </button>
                    </div>
                  );
                });
              })()
            )}
          </div>

          {/* Summary */}
          <div className="cq-grid-3" style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid var(--dp-border-subtle)',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--dp-text-primary)',
              }}>
                {textoOriginal.length.toLocaleString('pt-BR')}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                {t('textTools.original_chars')}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--dp-blue-500)',
              }}>
                {partesDivididas.length}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                {modoAtivo === 'partes' ? t('textTools.parts_created') : t('textTools.blocks_created')}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--dp-text-primary)',
              }}>
                ~{Math.round(textoOriginal.length / partesDivididas.length).toLocaleString('pt-BR')}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                {modoAtivo === 'partes' ? t('textTools.avg_per_part') : t('textTools.avg_per_block')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {partesDivididas.length === 0 && !textoDividido && textoOriginal.length > 0 && (
        <div style={{
          ...S.card,
          padding: '48px',
          textAlign: 'center',
          borderRadius: '16px',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(13, 174, 255, 0.15), rgba(59, 130, 246, 0.10))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Scissors size={36} style={{ color: 'var(--dp-blue-500)' }} />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 500,
            color: 'var(--dp-text-primary)',
            marginBottom: '8px',
          }}>
            {t('textTools.ready_to_split')}
          </h3>
          <p style={{ color: 'var(--dp-text-tertiary)', marginBottom: '24px' }}>
            {t('textTools.click_split_button')}
          </p>
        </div>
      )}

      {/* Info Cards */}
      {partesDivididas.length === 0 && !textoDividido && textoOriginal.length === 0 && (
        <div className="cq-grid-3" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
          <div className="dp-top-accent" style={S.card}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(13, 174, 255, 0.10)',
            }}>
              <FileText size={20} style={{ color: 'var(--dp-blue-500)' }} />
            </div>
            <h3 style={{
              fontWeight: 600,
              fontSize: '14px',
              color: 'var(--dp-text-primary)',
              marginBottom: '8px',
            }}>
              {t('textTools.paste_your_text')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
              {t('textTools.paste_desc')}
            </p>
          </div>

          <div className="dp-top-accent" style={S.card}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(167, 139, 250, 0.10)',
            }}>
              {modoAtivo === 'partes'
                ? <SplitSquareVertical size={20} style={{ color: 'var(--dp-purple-500, #A78BFA)' }} />
                : <Boxes size={20} style={{ color: 'var(--dp-purple-500, #A78BFA)' }} />
              }
            </div>
            <h3 style={{
              fontWeight: 600,
              fontSize: '14px',
              color: 'var(--dp-text-primary)',
              marginBottom: '8px',
            }}>
              {t('textTools.choose_mode')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
              {modoAtivo === 'partes'
                ? t('textTools.choose_desc_parts')
                : t('textTools.choose_desc_blocks')
              }
            </p>
          </div>

          <div className="dp-top-accent" style={S.card}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(52, 211, 153, 0.10)',
            }}>
              <Copy size={20} style={{ color: 'var(--dp-green-500, #34D399)' }} />
            </div>
            <h3 style={{
              fontWeight: 600,
              fontSize: '14px',
              color: 'var(--dp-text-primary)',
              marginBottom: '8px',
            }}>
              {t('textTools.copy_easily')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)' }}>
              {t('textTools.copy_desc')}
            </p>
          </div>
        </div>
      )}
    </>
  );

  // ============= MAIN RENDER =============
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        <PageHeader
          icon={<FileText size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('textTools.title')}
          description={t('textTools.subtitle')}
        />

        {/* Tool Tabs */}
        <div style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '22px',
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '12px',
          padding: '5px',
          width: 'fit-content',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 22px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === tab.key
                  ? 'linear-gradient(135deg, rgba(13, 174, 255, 0.12), rgba(13, 174, 255, 0.04))'
                  : 'transparent',
                color: activeTab === tab.key ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                fontSize: '13px',
                fontWeight: activeTab === tab.key ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                boxShadow: activeTab === tab.key
                  ? '0 0 14px rgba(13, 174, 255, 0.10), inset 0 0 12px rgba(13, 174, 255, 0.04)'
                  : 'none',
                fontFamily: 'var(--dp-font)',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content panels with animation */}
        <div key={activeTab} style={{ animation: 'fadeInUp 0.3s ease' }}>
          {activeTab === 'editor' && renderEditor()}
          {activeTab === 'srt' && renderSRT()}
          {activeTab === 'divider' && renderDivider()}
        </div>
      </main>

      {/* CSS animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default TextToolsPage;
